import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';

// material-ui
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

// icons
import { ChevronDown, ChevronRight } from 'lucide-react';

// project import
import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';

// ==============================|| NAVIGATION - LIST GROUP ||============================== //

export default function NavGroup({ item }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();
  const isDistributorNav = sessionStorage.getItem('company_type') === 'distributor' || /distributor/i.test(item?.id || '');

  const defaultOpenState = useMemo(() => {
    const state = {};

    if (!item?.children || !Array.isArray(item.children)) {
      return state;
    }

    item.children.forEach((menuItem, index) => {
      if (menuItem.type !== 'collapse' || !menuItem.children) return;

      const hasActiveChild = menuItem.children.some((child) => {
        const target = child.link || child.url;
        return target ? !!matchPath({ path: target, end: false }, pathname) : false;
      });

      if (hasActiveChild || (isDistributorNav && index === 0)) {
        state[menuItem.id] = true;
      }
    });

    return state;
  }, [isDistributorNav, item, pathname]);

  const [openCollapse, setOpenCollapse] = useState(defaultOpenState);

  useEffect(() => {
    setOpenCollapse(defaultOpenState);
  }, [defaultOpenState]);

  const handleToggle = (id) => {
    setOpenCollapse((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const navCollapse = item.children?.map((menuItem) => {
    switch (menuItem.type) {
      case 'collapse': {
        const Icon = menuItem.icon;
        const itemIcon = menuItem.icon ? (
          <Icon
            size={18}
            strokeWidth={1.8}
            style={{
              minWidth: '18px',
              minHeight: '18px'
            }}
          />
        ) : (
          false
        );
        const isOpen = !!openCollapse[menuItem.id];

        return (
          <Box key={menuItem.id} sx={{ mb: 0.6 }}>
            <ListItemButton
              onClick={() => handleToggle(menuItem.id)}
              sx={(theme) => ({
                pl: drawerOpen ? `${1 * 28}px` : 1.5,
                py: drawerOpen ? 1.1 : 1.25,
                mx: 0.75,
                borderRadius: 2,
                backgroundColor: isDistributorNav && isOpen ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                border: isDistributorNav && isOpen ? '1px solid rgba(59,130,246,0.2)' : '1px solid transparent',
                color: isDistributorNav && isOpen ? 'primary.main' : '#475467',
                '&:hover': {
                  backgroundColor: isDistributorNav ? 'rgba(99, 102, 241, 0.06)' : 'primary.lighter'
                }
              })}
            >
              {itemIcon && (
                <ListItemIcon
                  sx={{
                    minWidth: 28,
                    color: isDistributorNav && isOpen ? 'primary.main' : '#A0A5AE',
                    ...(!drawerOpen && {
                      borderRadius: 1.5,
                      width: 36,
                      height: 36,
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { bgcolor: 'secondary.lighter' }
                    })
                  }}
                >
                  {itemIcon}
                </ListItemIcon>
              )}

              {drawerOpen && (
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: isDistributorNav && isOpen ? 700 : 600,
                        color: isDistributorNav && isOpen ? 'primary.main' : '#475467',
                        letterSpacing: '0.01em'
                      }}
                    >
                      {menuItem.title}
                    </Typography>
                  }
                />
              )}

              {drawerOpen && (
                isOpen ? <ChevronDown size={16} color={isDistributorNav ? '#3b82f6' : '#A4A4A4'} /> : <ChevronRight size={16} color="#A4A4A4" />
              )}
            </ListItemButton>

            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: drawerOpen ? 1.5 : 0.5, pt: 0.6 }}>
                {menuItem.children?.map((child) => (
                  <NavItem key={child.id} item={child} level={2} isParents />
                ))}
              </List>
            </Collapse>
          </Box>
        );
      }

      case 'item':
        return <NavItem key={menuItem.id} item={menuItem} level={1} />;

      default:
        return (
          <Typography key={menuItem.id} variant="h6" color="error" align="center">
            Fix - Group Collapse or Items
          </Typography>
        );
    }
  });

  return (
    <List
      subheader={
        item.title &&
        drawerOpen && (
          <Box sx={{ pl: 3, mb: 1.5 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: '"Sofia Sans", sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#98A2B3' }}>
              {item.title}
            </Typography>
          </Box>
        )
      }
      sx={{ mb: drawerOpen ? 1.5 : 0, py: 0, zIndex: 0 }}
    >
      {navCollapse}
    </List>
  );
}

NavGroup.propTypes = {
  item: PropTypes.object
};
